// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {Link, useLocation} from "react-router-dom";

function BookingCard({ image, title, description, price, location, duration, action, bookId, buttonText }) {
  const search = useLocation().search;

  const renderUrl = (bookId) => {
    const id = new URLSearchParams(search).get('userid');
    if (id !== undefined && id !== null) {
      return `/amenities/book?id=${bookId}&userid=${id}`;
    } else {
      return `/amenities/book?id=${bookId}`;
    }
  };

  // Parse price to check if free
  const priceValue = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.-]+/g, "")) : price;
  const isFree = !priceValue || priceValue === 0;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Large Hero Image - 60% of card */}
      <MDBox
        position="relative"
        sx={{
          height: "220px",
          minHeight: "220px",
          overflow: "hidden",
        }}
      >
        <MDBox
          component="img"
          src={image}
          alt={title}
          width="100%"
          height="100%"
          sx={{
            objectFit: "cover",
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
        />
        {/* Price Badge */}
        <MDBox
          position="absolute"
          top={12}
          right={12}
          px={2}
          py={0.75}
          borderRadius="lg"
          sx={{
            backgroundColor: isFree ? "success.main" : "info.main",
            color: "white",
          }}
        >
          <MDTypography variant="button" fontWeight="bold" color="white" sx={{ fontSize: "14px" }}>
            {isFree ? "FREE" : price}
          </MDTypography>
        </MDBox>
        {/* Admin Actions */}
        {action && (
          <MDBox
            position="absolute"
            top={12}
            left={12}
            display="flex"
            gap={1}
          >
            {action}
          </MDBox>
        )}
      </MDBox>

      {/* Content Section */}
      <MDBox
        p={3}
        display="flex"
        flexDirection="column"
        flexGrow={1}
      >
        {/* Title - Large and Bold */}
        <MDTypography
          variant="h4"
          fontWeight="bold"
          sx={{
            fontSize: "1.5rem",
            lineHeight: 1.3,
            mb: 1,
            color: "dark.main",
          }}
        >
          {title}
        </MDTypography>

        {/* Description - Readable size */}
        <MDTypography
          variant="body1"
          color="text"
          sx={{
            fontSize: "1rem",
            lineHeight: 1.5,
            mb: 2,
            flexGrow: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {description}
        </MDTypography>

        {/* Duration Info - Clear with icon */}
        <MDBox
          display="flex"
          alignItems="center"
          mb={2}
          sx={{
            backgroundColor: "grey.100",
            borderRadius: "lg",
            px: 2,
            py: 1,
          }}
        >
          <Icon sx={{ color: "info.main", fontSize: "1.5rem", mr: 1 }}>schedule</Icon>
          <MDTypography variant="body1" fontWeight="medium" sx={{ fontSize: "1rem" }}>
            {duration} per session
          </MDTypography>
        </MDBox>

        {/* Book Now Button - Large and Prominent */}
        <MDButton
          component={Link}
          to={renderUrl(bookId)}
          variant="gradient"
          color="success"
          size="large"
          fullWidth
          sx={{
            py: 1.5,
            fontSize: "1.1rem",
            fontWeight: "bold",
            minHeight: "52px",
          }}
        >
          <Icon sx={{ mr: 1, fontSize: "1.3rem" }}>event_available</Icon>
          {buttonText}
        </MDButton>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of BookingCard
BookingCard.defaultProps = {
  action: false,
  buttonText: "Book Now",
};

// Typechecking props for the BookingCard
BookingCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  bookId: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  location: PropTypes.node.isRequired,
  buttonText: PropTypes.string,
  duration: PropTypes.string,
  action: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
};

export default BookingCard;
